import { useState, useEffect } from "react";
import { FormGroup, Row, Col, Label, Input } from "reactstrap";
import { Button, CustomForm } from "../Form/style";
import "../Form/style.css";
import "../../index.css";
import "react-intl-tel-input/dist/main.css";

import countriesData from "../../components/json/countriesStates.json";
import { toast } from "react-toastify";
import { clientService } from "../../services";
import TokenService from "../../services/token.service";
import IntlTelInput from "react-intl-tel-input";
import { PiExclamationMarkDuotone } from "react-icons/pi";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import AllCountries from "react-intl-tel-input/dist/components/AllCountries";

const phoneCountries = AllCountries.getCountries();
const dialCodeByIso = (iso2 = "") => {
  const match = phoneCountries.find(
    (item) => item.iso2 === String(iso2).toLowerCase()
  );
  return match?.dialCode || "";
};

const FILE_FIELDS = [
  "proofOfIdentity",
  "proofOfIdentityBack",
  "resProofOfAddress",
  "emiratesId",
];
const ALLOWED_FILE_EXT = ["jpg", "jpeg", "png", "pdf", "heic"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ENGLISH_REGEX = /^[a-zA-Z0-9\s.,'?!@#$%^&:*()_+=-]*$/;

const initialStep = {
  firstName: "",
  email: "",
  phone: "",
  accountType: "",
  accountTypeCurrency: "",
  country: "",
  countryCode: "",
  platform: "MT5",
  password: "",
  confirmPassword: "",
  address: "",
  proofOfIdentity: "",
  proofOfIdentityBack: "",
  resProofOfAddress: "",
  residency: "",
  residencyCode: "",
  taxIdentificationNumber: "",
  emiratesId: "",
  acceptTradingTerms: false,
  accountTradedByOthers: false,
  usCitizen: false,
  acknowledge: false,
  declaration: false,
};

const initialErrors = Object.keys(initialStep).reduce((acc, key) => {
  acc[key] = false;
  return acc;
}, {});

const isPasswordValid = (password = "") =>
  /[a-z]/.test(password) &&
  /[A-Z]/.test(password) &&
  /\d/.test(password) &&
  /[@#&$]/.test(password) &&
  password.length >= 8 &&
  password.length <= 12;

const PasswordRules = ({ password, t }) => {
  const rules = [
    { ok: /[a-z]/.test(password), label: t("PasswordLowercase") },
    { ok: /[A-Z]/.test(password), label: t("PasswordUppercase") },
    { ok: /\d/.test(password), label: t("PasswordNumber") },
    { ok: password.length >= 8, label: t("PasswordMinLength") },
    {
      ok: password.length >= 8 && password.length <= 12,
      label: t("PasswordMaxLength"),
    },
    { ok: /[@#&$]/.test(password), label: t("PasswordSpecialChar") },
  ];

  return (
    <div className="password-requirements">
      <h4>{t("PasswordRequirements")}</h4>
      {rules.map((rule) => (
        <p key={rule.label} className={rule.ok ? "valid" : "invalid"}>
          {rule.label}
        </p>
      ))}
    </div>
  );
};

export const MoroccoForm = ({ setLoading }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar" || i18n.language === "ir";

  const [refValue, setRefValue] = useState("");
  const [ptrValue, setPtrValue] = useState("");
  const [agreementId, setAgreementId] = useState("");
  const [entityValue, setEntityValue] = useState("");
  const [utmArray, setUtmArray] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [tradingAccount, setTradingAccount] = useState([]);
  const [accountTypesStatus, setAccountTypesStatus] = useState("loading");
  const [imageList, setImageList] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("971");
  const [emailCheck, setEmailCheck] = useState({ type: "", value: "" });
  const [debouncedCheck, setDebouncedCheck] = useState("");
  const [emailError, setEmailError] = useState(0);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({ step1: { ...initialStep } });
  const [validationErrors, setValidationErrors] = useState({
    step1: { ...initialErrors },
  });

  const step = formData.step1;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setRefValue(params.get("rm") || "");
    setPtrValue(params.get("ptr") || "");
    setAgreementId(params.get("agr") || "");
    setEntityValue(params.get("entity") || "");
    setUtmArray({
      utmCampaign: params.get("utm_campaign") || "",
      utmCategory: params.get("utm_category") || "",
      utmSource: params.get("utm_source") || "",
      utmMedium: params.get("utm_medium") || "",
    });
    setCountryList(countriesData);
    TokenService.setUser(process.env.REACT_APP_TOKEN);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCheck(emailCheck), 500);
    return () => clearTimeout(timer);
  }, [emailCheck]);

  useEffect(() => {
    if (!debouncedCheck?.value) return;

    const checkExists = async () => {
      try {
        const res = await clientService.emailExist(debouncedCheck);
        const exists = Boolean(res.agentId);
        if (debouncedCheck.type === "email") setEmailExists(exists);
        else setPhoneExists(exists);
      } catch (error) {
        console.error("Exist check failed:", error);
      }
    };

    checkExists();
  }, [debouncedCheck]);

  useEffect(() => {
    const loadAccounts = async () => {
      setAccountTypesStatus("loading");
      try {
        const accounts = await clientService.getAccountTypes({ showInCrm: 1 });
        const list = Array.isArray(accounts)
          ? accounts
          : accounts?.data || [];
        const mt5 = list.filter(
          (item) => String(item.platform || "").toUpperCase() === "MT5"
        );
        if (mt5.length) {
          setTradingAccount(mt5);
          setAccountTypesStatus("ready");
        } else {
          setTradingAccount([]);
          setAccountTypesStatus("unavailable");
        }
      } catch (error) {
        console.error("Account types failed:", error);
        setTradingAccount([]);
        setAccountTypesStatus("unavailable");
      }
    };
    loadAccounts();
  }, []);

  const updateField = (name, value) => {
    if (name === "email") {
      setEmailCheck({ type: "email", value });
      setEmailError(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 0 : 1);
    }

    if (name === "phone") {
      setEmailCheck({ type: "phone", value: value.replace(/\s+/g, "") });
      setFormData((prev) => ({
        ...prev,
        step1: { ...prev.step1, phone: value.replace(/[^\d]/g, "") },
      }));
      return;
    }

    const canSet =
      typeof value === "boolean" ||
      ENGLISH_REGEX.test(value) ||
      FILE_FIELDS.includes(name);

    if (!canSet) return;

    setFormData((prev) => ({
      ...prev,
      step1: { ...prev.step1, [name]: value },
    }));
  };

  const fieldClass = (key, value) =>
    !validationErrors.step1[key] || value ? "is-valid" : "is-invalid";

  const validate = () => {
    const {
      firstName,
      email,
      phone,
      platform,
      accountType,
      password,
      confirmPassword,
      country,
      address,
      proofOfIdentity,
      proofOfIdentityBack,
      resProofOfAddress,
      taxIdentificationNumber,
      emiratesId,
      acceptTradingTerms,
      accountTradedByOthers,
      usCitizen,
      acknowledge,
    } = step;

    const checks = {
      firstName: firstName.trim() !== "",
      email: email.trim() !== "",
      phone: phone.trim() !== "",
      platform: platform !== "",
      accountType: accountType !== "",
      password: isPasswordValid(password),
      confirmPassword: confirmPassword === password && confirmPassword !== "",
      country: country !== "",
      address: address.trim() !== "",
      proofOfIdentity: proofOfIdentity !== "",
      resProofOfAddress: Boolean(resProofOfAddress),
      taxIdentificationNumber: !usCitizen || taxIdentificationNumber !== "",
      emiratesId: selectedCountryCode !== "ae" || emiratesId !== "",
      acceptTradingTerms: Boolean(acceptTradingTerms),
      accountTradedByOthers: Boolean(accountTradedByOthers),
      acknowledge: selectedCountryCode !== "ae" || Boolean(acknowledge),
    };

    if (!proofOfIdentity && !resProofOfAddress) {
      toast.error("Please Upload the requested files");
    }
    if (selectedCountryCode === "ae" && !emiratesId) {
      toast.error("Please Upload Emirates Id");
    }

    const files = [proofOfIdentity, proofOfIdentityBack, resProofOfAddress, emiratesId];
    let docsOk = proofOfIdentity !== "";
    if (docsOk) {
      for (let i = 0; i < files.length; i++) {
        for (let j = i + 1; j < files.length; j++) {
          if (files[i] && files[i] === files[j]) {
            toast.error("Uploaded files could not be the same");
            docsOk = false;
          }
        }
      }
    }

    setValidationErrors({
      step1: {
        ...initialErrors,
        firstName: !checks.firstName,
        email: !checks.email,
        phone: !checks.phone,
        platform: !checks.platform,
        accountType: !checks.accountType,
        password: !checks.password,
        confirmPassword: !checks.confirmPassword,
        country: !checks.country,
        address: !checks.address,
        proofOfIdentity: !checks.proofOfIdentity,
        resProofOfAddress: !checks.resProofOfAddress,
        taxIdentificationNumber: usCitizen && !checks.taxIdentificationNumber,
        emiratesId: selectedCountryCode === "ae" && !checks.emiratesId,
        acceptTradingTerms: !checks.acceptTradingTerms,
        accountTradedByOthers: !checks.accountTradedByOthers,
        acknowledge: selectedCountryCode === "ae" && !checks.acknowledge,
      },
    });

    const isValid =
      checks.firstName &&
      checks.email &&
      checks.phone &&
      checks.accountType &&
      checks.password &&
      checks.confirmPassword &&
      checks.country &&
      checks.address &&
      docsOk &&
      checks.proofOfIdentity &&
      checks.acceptTradingTerms &&
      checks.accountTradedByOthers &&
      checks.taxIdentificationNumber &&
      checks.acknowledge;

    if (!isValid) toast.error("Please fill all the fields!");
    return isValid;
  };

  const updateImageList = (key, files) => {
    setImageList((prev) => {
      const index = prev.findIndex((item) => item.key === key);
      if (index === -1) return [...prev, { key, value: files }];
      const next = [...prev];
      next[index] = { key, value: files };
      return next;
    });
  };

  const handleFileUpload = (e, docKey) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = e.target.value.split(".").pop().toLowerCase();
    if (file.size > MAX_FILE_SIZE) {
      alert("File size exceeds the limit of 5 MB.");
      e.target.value = "";
      updateField(e.target.name, "");
      return;
    }
    if (!ALLOWED_FILE_EXT.includes(ext)) {
      alert("Please Upload files with extension jpg, jpeg, png, pdf, heic");
      e.target.value = "";
      updateField(e.target.name, "");
      return;
    }

    updateImageList(docKey, [file]);
    updateField(e.target.name, e.target.value);
  };

  const handleCountryChange = (option) => {
    if (!option?.value) return;
    const country = countriesData.find((item) => item.name === option.value);
    if (!country) return;

    const iso2 = String(country.iso2 || "").toLowerCase();
    setSelectedCountryCode(iso2);
    const dial = dialCodeByIso(iso2);
    if (dial) setPhoneDialCode(dial);
    updateField("country", country.name);
    updateField("countryCode", country.iso2);
    updateField("residency", country.name);
    updateField("residencyCode", country.iso2);
  };

  const buildPayload = () => ({
    agentId: refValue || "2",
    parentId: ptrValue,
    entity: entityValue || "FSC",
    firstName: step.firstName,
    email: step.email,
    platform: step.platform,
    accountType: step.accountType,
    accountTypeCurrency: step.accountTypeCurrency,
    country: step.countryCode || step.country,
    countryCode: step.countryCode,
    phone: (() => {
      const national = String(step.phone || "")
        .replace(/\D/g, "")
        .replace(/^0+/, "");
      return phoneDialCode ? `${phoneDialCode}${national}` : national;
    })(),
    password: step.password,
    address: step.address,
    ...utmArray,
    agreementId,
    proofOfIdentity: step.proofOfIdentity,
    proofOfIdentityBack: step.proofOfIdentityBack,
    residency: step.residency,
    residencyCode: step.residencyCode,
    resProofOfAddress: step.resProofOfAddress,
    taxIdentificationNumber: step.taxIdentificationNumber,
    accountTradedByOthers: step.accountTradedByOthers,
    acceptTradingTerms: step.acceptTradingTerms,
    usCitizen: step.usCitizen,
    declaration: step.declaration,
    acceptedTerms: true,
    acceptedRisk: true,
    acceptedPrivacy: true,
    acceptedExecutionPolicy: true,
    acceptedConflicts: true,
    accountStatus: "New",
    isLead: false,
    isClient: true,
  });

  const submitForm = async (e) => {
    e.preventDefault();

    if (emailExists) toast.error("Email Exist!");
    if (phoneExists) toast.error("Phone Exist!");
    if (!validate() || emailExists || phoneExists) return;

    setLoading("pending");
    try {
      const postClient = await clientService.singleRegistration(buildPayload());
      const created = Array.isArray(postClient) ? postClient[0] : postClient;
      const customerId = Number(
        created?.id ?? created?.customerId ?? created?.customer_id
      );

      if (!(created && !created.data && Number.isFinite(customerId) && customerId > 0)) {
        setLoading("new");
        toast.error(
          created?.data?.message ||
          postClient?.data?.message ||
          postClient?.message ||
          "Registration failed"
        );
        console.error("Lead response:", postClient);
        return;
      }

      if (imageList.length) {
        const docs = new FormData();
        docs.append("customerId", customerId);
        imageList.forEach((item) => {
          if (item?.value?.[0]) docs.append(item.key, item.value[0]);
        });

        const uploadRes = await clientService.uploadDocuments(docs);
        const uploadFailed =
          uploadRes?.status === false ||
          uploadRes?.isSuccess === false ||
          (Array.isArray(uploadRes) &&
            uploadRes[0]?.message &&
            !uploadRes[0]?.id);

        if (uploadFailed) {
          setLoading("new");
          toast.error(
            uploadRes?.message ||
            uploadRes?.[0]?.message ||
            "Documents upload failed"
          );
          console.error("Upload response:", uploadRes);
          return;
        }
      }

      setLoading("approved");
      toast.success("Successfully Registered!");
    } catch (error) {
      setLoading("new");
      toast.error(error?.message || "Registration failed, please try again!");
      console.error(error);
    }
  };

  const accountTypes = accountTypesStatus === "ready" ? tradingAccount : [];
  const accountTypePlaceholder =
    accountTypesStatus === "loading"
      ? "Loading..."
      : accountTypesStatus === "unavailable"
        ? "Not available"
        : "-- Select --";
  const countryOptions = countryList.map((item) => ({
    value: item.name,
    label: item.name,
  }));

  const agreementClass = (key, value) =>
    !validationErrors.step1[key] || value ? "text-black" : "text-danger";

  return (
    <div
      className={`formBody ${isRtl ? "is-rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
      style={{ fontSize: isRtl ? "18px" : "" }}
    >
      <CustomForm className="signup-form">
        <div className="justify mt-2 px-1 px-md-2">
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>{t("FullName")}</Label>
                <Input
                  className={`form-control ${fieldClass("firstName", step.firstName)}`}
                  name="firstName"
                  type="text"
                  placeholder="John"
                  value={step.firstName}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                    const capitalized = cleaned
                      .split(" ")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ");
                    updateField("firstName", capitalized);
                  }}
                />
                <small className="field-hint">{t("EnterNameNote")}</small>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>{t("EmailAddress")}</Label>
                <Input
                  className={`form-control ${fieldClass("email", step.email)}`}
                  type="email"
                  name="email"
                  value={step.email}
                  placeholder="john@gmail.com"
                  invalid={emailError === 1 && step.email !== ""}
                  valid={emailError === 0 && step.email !== ""}
                  onChange={(e) => updateField("email", e.target.value)}
                />
                {emailExists && <span className="text-danger">Email Already Exist</span>}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("Platform")}</Label>
                <Input
                  type="select"
                  name="platform"
                  className={fieldClass("platform", step.platform)}
                  value={step.platform}
                  onChange={(e) => {
                    updateField("platform", e.target.value);
                    updateField("accountType", "");
                  }}
                >
                  <option value="MT5">MT5</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("AccountType")}</Label>
                <Input
                  type="select"
                  name="accountType"
                  className={fieldClass("accountType", step.accountType)}
                  value={step.accountType}
                  disabled={accountTypesStatus !== "ready"}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const selected = accountTypes.find(
                      (item) => String(item.id) === e.target.value
                    );
                    updateField("accountType", e.target.value);
                    if (selected?.currency) {
                      updateField("accountTypeCurrency", selected.currency);
                    }
                  }}
                >
                  <option value="">{accountTypePlaceholder}</option>
                  {accountTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row className="password-block">
            <Col md={8}>
              <Row>
                <Col md={6} className="position-relative">
                  <FormGroup>
                    <Label>{t("Password")}</Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={`form-control ${fieldClass("password", isPasswordValid(step.password))}`}
                      placeholder="***********"
                      minLength={8}
                      maxLength={12}
                      value={step.password}
                      onChange={(e) => {
                        if (e.target.value.length < 13) updateField("password", e.target.value);
                      }}
                    />
                    {showPassword ? (
                      <FaRegEye className="password-toggle" onClick={() => setShowPassword(false)} />
                    ) : (
                      <FaRegEyeSlash className="password-toggle" onClick={() => setShowPassword(true)} />
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} className="position-relative">
                  <FormGroup>
                    <Label>{t("ConfirmPassword")}</Label>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className={`form-control ${fieldClass(
                        "confirmPassword",
                        step.confirmPassword && step.confirmPassword === step.password
                      )}`}
                      placeholder="***********"
                      minLength={8}
                      maxLength={12}
                      value={step.confirmPassword}
                      onChange={(e) => {
                        if (e.target.value.length < 13) {
                          updateField("confirmPassword", e.target.value);
                        }
                      }}
                    />
                    {showConfirmPassword ? (
                      <FaRegEye
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(false)}
                      />
                    ) : (
                      <FaRegEyeSlash
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(true)}
                      />
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>{t("CountryOfResidence")}</Label>
                    <Select
                      className={`country-select ${fieldClass("country", step.country)}`}
                      name="country"
                      options={countryOptions}
                      value={countryOptions.find((o) => o.value === step.country) || null}
                      onChange={handleCountryChange}
                      placeholder="-- Select --"
                    />
                  </FormGroup>
                </Col>
                <Col md={6} className="phoneNum">
                  <Label>{t("PhoneNumber")}</Label>
                  <IntlTelInput
                    key={selectedCountryCode || "ae"}
                    name="phone"
                    containerClassName="intl-tel-input"
                    inputClassName={`form-control ${fieldClass("phone", step.phone)}`}
                    style={{ width: "100%" }}
                    maxLength={15}
                    format="true"
                    defaultCountry={selectedCountryCode || "ae"}
                    useMobileFullscreenDropdown={false}
                    value={step.phone}
                    onSelectFlag={(currentNumber, countryData) => {
                      if (countryData?.dialCode) {
                        setPhoneDialCode(String(countryData.dialCode));
                      }
                    }}
                    onPhoneNumberChange={(status, value, countryData) => {
                      if (countryData?.dialCode) {
                        setPhoneDialCode(String(countryData.dialCode));
                      }
                      updateField("phone", value);
                    }}
                  />
                  {phoneExists && <span className="text-danger">Phone Already Exist</span>}
                </Col>
              </Row>
            </Col>

            <Col md={4}>
              <PasswordRules password={step.password} t={t} />
            </Col>
          </Row>

          <div className="section-divider">
            <h3 className="section-title">{t("PersonalInformation")}</h3>
          </div>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>{t("CurrentAddress")}</Label>
                <Input
                  className={`form-control ${fieldClass("address", step.address)}`}
                  name="address"
                  type="text"
                  placeholder="House No - Street - City - State"
                  value={step.address}
                  onChange={(e) => updateField("address", e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="upload-row z-50">
            <Col md={4}>
              <div className="upload-field">
                <Label htmlFor="proofOfIdentity" className="upload-label">
                  {t("ProofOfIdentity")}
                  <span className="info-tip">
                    <PiExclamationMarkDuotone style={{ rotate: "180deg" }} />
                    <span className={isRtl ? "info-tip-text rtl" : "info-tip-text"}>
                      {t("POIToolTipHeading")} <b>{t("One")}</b> {t("POIToolTipEnding")}
                      <ul>
                        <li>{t("POIToolTipList1")}</li>
                        <li>{t("POIToolTipList2")}</li>
                        <li>{t("POIToolTipList3")}</li>
                      </ul>
                    </span>
                  </span>
                </Label>
                <Input
                  className={`form-control upload-input ${fieldClass(
                    "proofOfIdentity",
                    step.proofOfIdentity
                  )}`}
                  type="file"
                  name="proofOfIdentity"
                  id="proofOfIdentity"
                  accept=".jpg, .jpeg, .png, .pdf, .heic"
                  onChange={(e) => handleFileUpload(e, "PROOF_OF_ID")}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="upload-field">
                <Label htmlFor="proofOfIdentityBack" className="upload-label">
                  {t("ProofOfIdentityBack")}
                </Label>
                <Input
                  className="form-control upload-input"
                  type="file"
                  name="proofOfIdentityBack"
                  id="proofOfIdentityBack"
                  accept=".jpg, .jpeg, .png, .pdf, .heic"
                  onChange={(e) => handleFileUpload(e, "PROOF_OF_ID_BACK")}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="upload-field">
                <Label htmlFor="resProofOfAddress" className="upload-label">
                  {t("ProofOfAddress")}
                  <span className="info-tip">
                    <PiExclamationMarkDuotone style={{ rotate: "180deg" }} />
                    <span className={isRtl ? "info-tip-text rtl end" : "info-tip-text end"}>
                      {t("ToolTipHeading")} <b>{t("ToolTipBoldHeading")} </b>
                      {t("ToolTipEnding")}
                      <ul>
                        <li>{t("ToolTipList1")}</li>
                        <li>{t("ToolTipList2")}</li>
                        <li>{t("ToolTipList3")}</li>
                        <li>{t("ToolTipList4")}</li>
                        <li>{t("ToolTipList5")}</li>
                        <li>{t("ToolTipList6")}</li>
                      </ul>
                    </span>
                  </span>
                </Label>
                <Input
                  className={`form-control upload-input ${fieldClass(
                    "resProofOfAddress",
                    step.resProofOfAddress
                  )}`}
                  name="resProofOfAddress"
                  id="resProofOfAddress"
                  type="file"
                  accept=".jpg, .jpeg, .png, .pdf, .heic"
                  onChange={(e) => handleFileUpload(e, "RES_PROOF_OF_ADDRESS")}
                />
              </div>
            </Col>

            {selectedCountryCode === "ae" && (
              <Col md={4}>
                <div className="upload-field">
                  <Label htmlFor="emiratesId" className="upload-label">
                    {t("EMIRATESID")}
                  </Label>
                  <Input
                    className={`form-control upload-input ${fieldClass(
                      "emiratesId",
                      step.emiratesId
                    )}`}
                    name="emiratesId"
                    id="emiratesId"
                    type="file"
                    accept=".jpg, .jpeg, .png, .pdf, .heic"
                    onChange={(e) => handleFileUpload(e, "EMIRATES_ID")}
                  />
                </div>
              </Col>
            )}

            <Col md={4}>
              <div className="upload-field">
                <Label>{t("UsCitizen")}</Label>
                <div className="us-citizen-options">
                  <label className="citizen-option" htmlFor="usCitizenYes">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="usCitizen"
                      id="usCitizenYes"
                      checked={step.usCitizen === true}
                      onChange={() => updateField("usCitizen", true)}
                    />
                    <span>{t("Yes")}</span>
                  </label>
                  <label className="citizen-option" htmlFor="usCitizenNo">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="usCitizen"
                      id="usCitizenNo"
                      checked={step.usCitizen === false}
                      onChange={() => updateField("usCitizen", false)}
                    />
                    <span>{t("No")}</span>
                  </label>
                </div>
              </div>
            </Col>

            {step.usCitizen && (
              <Col md={4}>
                <div className="upload-field">
                  <Label>{t("SSN")}</Label>
                  <Input
                    className={`form-control ${!validationErrors.step1.taxIdentificationNumber
                      ? "is-valid"
                      : "is-invalid"
                      }`}
                    name="taxIdentificationNumber"
                    type="text"
                    value={step.taxIdentificationNumber}
                    placeholder="Enter SSN"
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        updateField("taxIdentificationNumber", e.target.value);
                      }
                    }}
                  />
                </div>
              </Col>
            )}
          </Row>

          <Row
            className={`${agreementClass(
              "accountTradedByOthers",
              step.accountTradedByOthers
            )} mt-4`}
          >
            <Col md={12}>
              <label className="agreement-row" htmlFor="accountTradedByOthers">
                <Input
                  className="agreement-check"
                  type="checkbox"
                  name="accountTradedByOthers"
                  id="accountTradedByOthers"
                  checked={Boolean(step.accountTradedByOthers)}
                  onChange={(e) =>
                    updateField("accountTradedByOthers", e.target.checked)
                  }
                />
                <span className="agreement-text">
                  {t("Agreement")}{" "}
                  <a
                    href="https://godocm.com/fr/limited-power-of-attorney-account-manager"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("PowerofAttorney")}
                  </a>
                </span>
              </label>
            </Col>

            <Col md={12}>
              <label
                className={`agreement-row ${agreementClass(
                  "acceptTradingTerms",
                  step.acceptTradingTerms
                )}`}
                htmlFor="acceptTradingTerms"
              >
                <Input
                  className="agreement-check"
                  type="checkbox"
                  name="acceptTradingTerms"
                  id="acceptTradingTerms"
                  checked={Boolean(step.acceptTradingTerms)}
                  onChange={(e) =>
                    updateField("acceptTradingTerms", e.target.checked)
                  }
                />
                <span className="agreement-text">
                  {t("Approve")}{" "}
                  <a
                    href="https://www.godocm.com/wp-content/uploads/2025/01/Terms-of-Business.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("TermsofBusiness")}
                  </a>{" "}
                  {t("Approve2")}
                </span>
              </label>
            </Col>

            {selectedCountryCode === "ae" && (
              <Col md={12}>
                <label
                  className={`agreement-row ${agreementClass(
                    "acknowledge",
                    step.acknowledge
                  )}`}
                  htmlFor="acknowledge"
                >
                  <Input
                    className="agreement-check"
                    type="checkbox"
                    name="acknowledge"
                    id="acknowledge"
                    checked={Boolean(step.acknowledge)}
                    onChange={(e) => updateField("acknowledge", e.target.checked)}
                  />
                  <span className="agreement-text">{t("GODOIFSLLC")}</span>
                </label>
              </Col>
            )}
          </Row>
        </div>

        <div className="submit-wrap">
          <Button type="submit" onClick={submitForm} className="submit-btn">
            {t("Submit")}
          </Button>
        </div>
      </CustomForm>
    </div>
  );
};
